/**
 * OWLY User Types
 *
 * Tipos para usuarios internos del equipo OWLY
 */

import { UserRole } from '@/shared/types/sgi-roles.types';

export enum OwlyUserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ON_LEAVE = 'on_leave',
}

export enum OwlyDepartment {
  SALES = 'sales',
  SUPPORT = 'support',
  DEVELOPMENT = 'development',
  ANALYTICS = 'analytics',
  OPERATIONS = 'operations',
  MANAGEMENT = 'management',
}

/**
 * Métricas de actividad de un usuario OWLY
 */
export interface OwlyUserMetrics {
  /** Compañías asignadas (para Sales/Support) */
  assignedCompanies: number;

  /** Tickets resueltos (para Support) */
  resolvedTickets?: number;

  /** Deals cerrados este mes (para Sales) */
  closedDealsThisMonth?: number;

  /** Revenue generado (para Sales) */
  revenueGenerated?: number;

  /** Último login */
  lastLogin: string;

  /** Tiempo promedio de respuesta (para Support) */
  avgResponseTime?: string;
}

/**
 * Job titles específicos del equipo OWLY
 */
export type OwlyJobTitle =
  | 'CEO'
  | 'CTO'
  | 'Head of Sales'
  | 'Customer Success Manager'
  | 'Product Manager'
  | 'Marketing Manager'
  | 'DevOps Engineer'
  | 'Backend Developer'
  | 'Frontend Developer'
  | 'Data Analyst'
  | 'Support Engineer';

/**
 * Usuario interno de OWLY
 */
export interface OwlyUser {
  /** ID único */
  id: string;

  /** Nombre completo */
  name: string;

  /** Email */
  email: string;

  /** Avatar URL */
  avatar?: string;

  /** Rol en OWLY */
  role: UserRole;

  /** Job Title específico (para filtrado de permisos) */
  jobTitle: OwlyJobTitle;

  /** Departamento */
  department: OwlyDepartment;

  /** Estado */
  status: OwlyUserStatus;

  /** Fecha de ingreso */
  joinedAt: string;

  /** Teléfono */
  phone?: string;

  /** Métricas de actividad */
  metrics: OwlyUserMetrics;

  /** Permisos especiales adicionales */
  specialPermissions?: string[];

  /** Notas internas */
  internalNotes?: string;
}

/**
 * Resumen del equipo OWLY
 */
export interface OwlyTeamSummary {
  /** Total de miembros del equipo */
  total: number;

  /** Miembros activos */
  active: number;

  /** Por departamento */
  byDepartment: Record<OwlyDepartment, number>;

  /** Por rol */
  byRole: Record<string, number>;
}
